import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import Blog from "./pages/Blog";
import Footer from "./components/Footer";
import PerformanceDashboard from "./components/common/PerformanceDashboard";
import AIMarketingDashboard from "./components/common/AIMarketingDashboard";
import AIMarbleChatbot from "./components/common/AIMarbleChatbot";


// Lazy load pages to reduce main bundle size
import { 
  LazyProducts, 
  LazyAbout, 
  LazyContact, 
  LazyCategoryPage, 
  LazyCategoriesList,
  LazyAdminGallery,
  LazyProductDetail
} from "./components/common/LazyPages";

// Import accessibility components and styles
import { SkipLinks, LiveRegion } from "./components/common/AccessibleComponents";
import { useAccessibleTheme, useScreenReaderAnnouncements } from "./hooks/useAccessibility";
import './styles/accessibility.css';

// Import PWA Manager and Performance Monitor
import './utils/pwaManager.js';
import './utils/performanceMonitor.js';

function App() {
  const [showPerformanceDashboard, setShowPerformanceDashboard] = useState(false);
  const [showAIMarketingDashboard, setShowAIMarketingDashboard] = useState(false);
  const [showAIChatbot, setShowAIChatbot] = useState(false);
  const { themeClasses } = useAccessibleTheme();
  const { announcement, announce } = useScreenReaderAnnouncements();

  useEffect(() => {
    // Announce page load for screen readers
    announce('Makrana Marble website has loaded', 'polite');
    
    // Initialize PWA features after app loads
    const initializePWA = async () => {
      try {
        // Check if PWA manager is available
        if (window.pwaManager) {
          console.log('PWA Manager is ready');
          
          // Log installation metrics for debugging
          const metrics = window.pwaManager.getInstallationMetrics();
          console.log('PWA Metrics:', metrics);
        }
        
        // Initialize performance monitoring
        if (window.performanceMonitor) {
          console.log('Performance Monitor is ready');
          
          // Track initial page view
          window.performanceMonitor.trackPageView();
          
          // Track app initialization
          window.performanceMonitor.trackCustomEvent('app-initialized', {
            timestamp: Date.now(),
            userAgent: navigator.userAgent,
            viewport: {
              width: window.innerWidth,
              height: window.innerHeight
            }
          });
        }
        
        // Handle app shortcuts and protocol handlers
        if ('launchQueue' in window) {
          window.launchQueue.setConsumer((launchParams) => {
            if (launchParams.targetURL) {
              // Handle custom protocol or file opening
              console.log('App launched with URL:', launchParams.targetURL);
              if (window.performanceMonitor) {
                window.performanceMonitor.trackCustomEvent('app-launched-with-url', {
                  url: launchParams.targetURL
                });
              }
            }
          });
        }
        
        // Handle share target
        if ('serviceWorker' in navigator && window.location.search.includes('share-target')) {
          const urlParams = new URLSearchParams(window.location.search);
          const sharedTitle = urlParams.get('title');
          const sharedText = urlParams.get('text');
          const sharedUrl = urlParams.get('url');
          
          if (sharedTitle || sharedText || sharedUrl) {
            console.log('Received shared content:', { sharedTitle, sharedText, sharedUrl });
            if (window.performanceMonitor) {
              window.performanceMonitor.trackCustomEvent('content-shared', {
                title: sharedTitle,
                text: sharedText,
                url: sharedUrl
              });
            }
          }
        }
        
        // Setup keyboard shortcuts for development and accessibility
        const handleKeyPress = (event) => {
          // Ctrl+Shift+P opens performance dashboard
          if (event.ctrlKey && event.shiftKey && event.key === 'P') {
            event.preventDefault();
            setShowPerformanceDashboard(true);
            announce('Performance dashboard opened');
            if (window.performanceMonitor) {
              window.performanceMonitor.trackCustomEvent('dashboard-opened', {
                method: 'keyboard-shortcut'
              });
            }
          }
          
          // Ctrl+Shift+M opens AI marketing dashboard
          if (event.ctrlKey && event.shiftKey && event.key === 'M') {
            event.preventDefault();
            setShowAIMarketingDashboard(true);
            announce('AI Marketing dashboard opened');
            if (window.performanceMonitor) {
              window.performanceMonitor.trackCustomEvent('ai-marketing-dashboard-opened', {
                method: 'keyboard-shortcut'
              });
            }
          }
          
          // Alt+1 focuses main content
          if (event.altKey && event.key === '1') {
            event.preventDefault();
            const mainContent = document.getElementById('main-content');
            if (mainContent) {
              mainContent.focus();
              announce('Focused on main content');
            }
          }
          
          // Alt+2 focuses navigation
          if (event.altKey && event.key === '2') {
            event.preventDefault();
            const navigation = document.getElementById('main-navigation');
            if (navigation) {
              navigation.focus();
              announce('Focused on navigation');
            }
          }
        };
        
        document.addEventListener('keydown', handleKeyPress);
        
        return () => {
          document.removeEventListener('keydown', handleKeyPress);
        };
        
      } catch (error) {
        console.error('PWA initialization error:', error);
        if (window.performanceMonitor) {
          window.performanceMonitor.trackCustomEvent('initialization-error', {
            error: error.message,
            stack: error.stack
          });
        }
      }
    };
    
    // Initialize after a short delay to ensure DOM is ready
    setTimeout(initializePWA, 1000);
    
    // Cleanup function
    return () => {
      // Any cleanup if needed
    };
  }, [announce]);

  // Track route changes and announce to screen readers
  useEffect(() => {
    const trackRouteChange = () => {
      if (window.performanceMonitor) {
        window.performanceMonitor.trackPageView();
      }
      
      // Announce route changes to screen readers
      const path = window.location.pathname;
      const pageName = path === '/' ? 'Home' : 
                      path.startsWith('/products') ? 'Products' :
                      path.startsWith('/about') ? 'About' :
                      path.startsWith('/contact') ? 'Contact' :
                      path.startsWith('/category') ? 'Category' :
                      path.startsWith('/categories') ? 'Categories' : 'Page';
      announce(`Navigated to ${pageName} page`);
    };
    
    // Track initial route
    trackRouteChange();
  }, [announce]);

  return (
    <HelmetProvider>
      <Router>
        <div className={`flex flex-col min-h-screen ${themeClasses.highContrast} ${themeClasses.reducedMotion} keyboard-nav`}>
          {/* Skip Links for Accessibility */}
          <SkipLinks />
          
          {/* Live Region for Screen Reader Announcements */}
          <LiveRegion announcement={announcement} />
          
          <Navbar />
          <main id="main-content" className="flex-grow" tabIndex="-1" role="main">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/products" element={<LazyProducts useSkeletonLoader={true} />} />
              <Route path="/about" element={<LazyAbout useSkeletonLoader={true} />} />
              <Route path="/contact" element={<LazyContact useSkeletonLoader={true} />} />
              
              {/* Blog routes */}
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:category" element={<Blog />} />
              <Route path="/blog/post/:slug" element={<Blog />} />
              
              {/* Category routes */}
              <Route path="/category/:category" element={<LazyCategoryPage useSkeletonLoader={true} />} />
              <Route path="/categories" element={<LazyCategoriesList useSkeletonLoader={true} />} />
              <Route path="/admin" element={<LazyAdminGallery useSkeletonLoader={true} />} />
              <Route path="/product/:id" element={<LazyProductDetail useSkeletonLoader={true} />} />

            </Routes>
          </main>
          <Footer />
          
          {/* Performance Dashboard Modal */}
          <PerformanceDashboard 
            isOpen={showPerformanceDashboard}
            onClose={() => setShowPerformanceDashboard(false)}
          />
          
                    {/* AI Marketing Dashboard Modal */}
          <AIMarketingDashboard 
            isOpen={showAIMarketingDashboard}
            onClose={() => setShowAIMarketingDashboard(false)}
          />
          
          {/* AI Marble Chatbot Modal */}
          <AIMarbleChatbot 
            isOpen={showAIChatbot}
            onClose={() => setShowAIChatbot(false)}
          />
          
          {/* Development Tools */}
          {process.env.NODE_ENV === 'development' && (
            <div className="fixed top-4 right-4 z-40 space-y-2">
              <button
                onClick={() => setShowPerformanceDashboard(true)}
                className="block bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors shadow-lg"
                title="Open Performance Dashboard (Ctrl+Shift+P)"
              >
                📊 Perf
              </button>
              <button
                onClick={() => setShowAIMarketingDashboard(true)}
                className="block bg-purple-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-purple-700 transition-colors shadow-lg"
                title="Open AI Marketing Dashboard (Ctrl+Shift+M)"
              >
                🤖 AI Marketing
              </button>
              <button
                onClick={() => setShowAIChatbot(true)}
                className="block bg-orange-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-orange-700 transition-colors shadow-lg"
                title="Open AI Chatbot"
              >
                💬 Chatbot
              </button>
            </div>
          )}
          
          {/* Customer AI Assistant Button */}
          <button
            onClick={() => setShowAIChatbot(true)}
            className="fixed bottom-6 right-6 z-40 bg-gradient-to-r from-orange-500 to-red-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 animate-pulse"
            title="Get Instant Marble Price Quote"
          >
            <div className="flex items-center gap-2">
              <span className="text-xl">💰</span>
              <span className="hidden sm:block font-medium">Price Calculator</span>
            </div>
          </button>
          
          {/* AI Marketing Button - Admin Only */}
          {process.env.NODE_ENV === 'production' && (
            <button
              onClick={() => {
                const password = prompt("Enter admin password:");
                if (password === "makrana2025admin") {
                  setShowAIMarketingDashboard(true);
                } else if (password !== null) {
                  alert("Incorrect password. Contact admin for access.");
                }
              }}
              className="fixed top-4 right-4 z-40 bg-gradient-to-r from-gray-600 to-gray-700 text-white px-3 py-2 rounded-lg text-sm hover:bg-gray-800 transition-all shadow-lg opacity-50 hover:opacity-100"
              title="Admin Panel - AI Marketing"
            >
              <div className="flex items-center gap-1">
                <span className="text-sm">👨‍💼</span>
                <span className="text-xs">Admin</span>
              </div>
            </button>
          )}
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
